import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { ColumnApi, GridApi, ValueFormatterParams } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import dayjs from 'dayjs';
import { AppState } from '$/store';
import { removeNews } from '$/store/news/admin/actions';
import { publishNews } from '$/store/news/admin/actions';
import BixButton from '$/components/global/bixButton';
import Loading from '$/components/loading/Loading';
import Modal from '$/components/global/modal';
import DeleteButton from '$/components/grid/DeleteButton';
import PublishButton from '$/components/grid/PublishButton';
import SortFilter, { ISortItem } from '$/components/sortFilter';
import ResultsCounter from '$/components/resultsCounter';
import { IFilterItem } from '$/components/sortFilter/filterItem';
import * as Bixby from '$/core';
import { NewsAdminService } from '$/services';
import { getCreditSightsNewsIntegrationEnabled } from '$/store/release-feature-flags/selectors';
import './CompanyNews.scss';

interface ICompanyNewsProps {
  filters: IFilterItem[];
  settings?: Bixby.Models.Settings;
  currentCompany?: Bixby.Models.AdminCompany;
  isNewsLoading: boolean;
  isCompanyLoading: boolean;
  isCreditSightsNewsIntegrationEnabled: boolean;
  dispatchRemoveNews: (news: Bixby.Models.AdminNews, retainTranscription: boolean) => Promise<void>;
  dispatchPublishNews: (news: Bixby.Models.AdminNews) => Promise<void>;
}

const CompanyNews: React.FC<ICompanyNewsProps> = props => {
  const {
    filters,
    settings,
    currentCompany,
    isNewsLoading,
    isCompanyLoading,
    isCreditSightsNewsIntegrationEnabled,
    dispatchRemoveNews,
    dispatchPublishNews,
  } = props;

  const [deleteModalActive, setDeleteModalActive] = useState(false);
  const [publishModalActive, setPublishModalActive] = useState(false);
  const [newsToDelete, setNewsToDelete] = useState({} as Bixby.Models.AdminNews | undefined);
  const [newsToPublish, setNewsToPublish] = useState({} as Bixby.Models.AdminNews | undefined);
  const [retainTranscription, setRetainTranscription] = useState(false);
  const [, setGridApi] = useState(null as GridApi | null);
  const [, setGridColumnApi] = useState(null as ColumnApi | null);

  const [sorts, setSorts] = useState([] as Array<ISortItem<any>>);
  const [activeSort, setActiveSort] = useState(Bixby.Enums.NewsEarningsSort.All as Bixby.Enums.NewsEarningsSort);
  const [filterTypes] = useState(filters as Array<IFilterItem>);
  const [activeFilterTypes, setActiveFilterTypes] = useState([] as Array<string>);
  const [news, setNews] = useState([] as Array<Bixby.Models.News>);
  const [filteredNewsEarnings, setFilteredNewsEarnings] = useState([] as Array<Bixby.Models.News>);

  useEffect(() => {
    if (settings) {
      const initial: Array<ISortItem<any>> = [
        { name: 'View All News', value: Bixby.Enums.NewsEarningsSort.All },
        { name: `Last ${settings.newsCutoff} days`, value: Bixby.Enums.NewsEarningsSort.LastXDays },
      ];
      setSorts(initial);
    }
  }, [settings]);

  // Original implementation has all the filtering and sorting done in-memory
  // Eventually we can refactor this to fetch incrementally
  const getNewsAsync = useCallback(async () => {
    let result: Bixby.Models.News[];

    if (currentCompany) {
      result = await NewsAdminService.getNewsByCompanyId(currentCompany.id);
    } else {
      result = await NewsAdminService.getNews();
    }

    setNews(result);
    setFilteredNewsEarnings(result);
  }, [currentCompany]);

  useEffect(() => {
    getNewsAsync();
  }, [getNewsAsync]);

  const handleFilter = (id: string, isFilterActive: boolean) => {
    if (isFilterActive) {
      setActiveFilterTypes([...activeFilterTypes, id]);
    } else {
      const indexOfFilter = activeFilterTypes.findIndex(_ => _ === id);
      if (indexOfFilter > -1) {
        activeFilterTypes.splice(indexOfFilter, 1);
        setActiveFilterTypes([...activeFilterTypes]);
      }
    }
  };

  const handleClearFilters = () => {
    setActiveFilterTypes([]);
  };

  const handleSort = (_event: any, id: Bixby.Enums.NewsEarningsSort) => {
    setActiveSort(id);
  };

  useEffect(() => {
    let result: Array<Bixby.Models.News> = news;

    // get news by sort
    if (settings && activeSort == Bixby.Enums.NewsEarningsSort.LastXDays) {
      const daysAgo = dayjs().subtract(settings.newsCutoff, 'days');
      result = news.filter(_ => Bixby.Utils.utcDateAsLocal(_.date).isAfter(daysAgo, 'days'));
    }

    // filter news
    result =
      activeFilterTypes.length > 0
        ? result.filter(n => activeFilterTypes.findIndex(f => f === n.newsTypeId) > -1)
        : result;

    setFilteredNewsEarnings(result);
  }, [news, settings, activeSort, activeFilterTypes]);

  const handleDeleteNews = async () => {
    if (newsToDelete && !Bixby.Utils.isNullOrEmpty(newsToDelete)) {
      await dispatchRemoveNews(newsToDelete, retainTranscription);

      if (currentCompany && !Bixby.Utils.isNullOrEmpty(currentCompany)) {
        await getNewsAsync();
      }

      setNewsToDelete(undefined);
    }
    setRetainTranscription(false);
    setDeleteModalActive(false);
  };

  const handlePublishNews = async () => {
    if (newsToPublish && !Bixby.Utils.isNullOrEmpty(newsToPublish)) {
      await dispatchPublishNews(newsToPublish);
      setNewsToPublish(undefined);
    }

    setPublishModalActive(false);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalActive(false);
    setNewsToDelete(undefined);
    setRetainTranscription(false);
  };

  const handleClosePublishModal = () => {
    setPublishModalActive(false);
    setNewsToPublish(undefined);
  };

  const getContextMenuItems = () => {
    const result = ['copy', 'copyWithHeaders', 'separator', 'export'];
    return result;
  };

  const onGridReady = (params: any) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  const dateValueFormatter = (params: ValueFormatterParams) => {
    if (params.value) {
      const newsDate = Bixby.Utils.utcDateAsLocal(params.value);
      return Bixby.Utils.formatDate(newsDate);
    } else {
      return '';
    }
  };

  const deleteButtonCellRenderer = (props: any) => {
    const handleDeleteButton = () => {
      setNewsToDelete(props.data);
      setDeleteModalActive(true);
      return Promise.resolve();
    };
    return (
      <DeleteButton row={props.data} deleter={handleDeleteButton} disabled={props.data.restrictRepublishOrDelete} />
    );
  };

  const publishButtonCellRenderer = (props: any) => {
    const handlePublishButton = () => {
      setNewsToPublish(props.data);
      setPublishModalActive(true);
      return Promise.resolve();
    };
    return (
      <PublishButton row={props.data} publisher={handlePublishButton} disabled={props.data.restrictRepublishOrDelete} />
    );
  };

  const renderNews = () => {
    if (isNewsLoading || isCompanyLoading) {
      return <Loading pageLevel />;
    } else {
      return (
        <div>
          <SortFilter
            filters={filterTypes}
            onFilter={handleFilter}
            sorts={sorts}
            onSort={handleSort}
            onClearFilters={handleClearFilters}
          />

          <ResultsCounter amount={(filteredNewsEarnings || []).length} />

          {filteredNewsEarnings && !Bixby.Utils.isNullOrEmpty(filteredNewsEarnings) ? (
            <div className="company_news_data_grid">
              <div className="grid_container">
                <div className="grid ag-theme-alpine">
                  <AgGridReact
                    enableRangeSelection={true}
                    getContextMenuItems={getContextMenuItems}
                    defaultColDef={{
                      flex: 1,
                      minWidth: 100,
                      sortable: true,
                      resizable: true,
                      lockPinned: true,
                      cellClass: 'align-center',
                    }}
                    onGridReady={onGridReady}
                    rowData={filteredNewsEarnings}
                    suppressDragLeaveHidesColumns={true}
                    suppressMovableColumns={true}
                    animateRows={true}
                    rowHeight={32}
                    headerHeight={32}
                    components={{
                      deleteButtonCellRenderer,
                      publishButtonCellRenderer,
                    }}
                    columnDefs={[
                      { maxWidth: 80, cellRenderer: 'deleteButtonCellRenderer' },
                      {
                        maxWidth: 225,
                        cellRenderer: 'publishButtonCellRenderer',
                        hide: !isCreditSightsNewsIntegrationEnabled,
                        headerName: 'Republish to CreditSights',
                        cellClass: 'align-center',
                      },
                      { field: 'referenceId', headerName: 'Reference Id', maxWidth: 150, cellClass: 'align-left' },
                      { field: 'date', headerName: 'Date', maxWidth: 150, valueFormatter: dateValueFormatter },
                      { field: 'subject', headerName: 'Subject', minWidth: 250, cellClass: 'align-left' },
                      { field: 'description', headerName: 'Description', minWidth: 250, cellClass: 'align-left' },
                    ]}></AgGridReact>
                </div>
              </div>
            </div>
          ) : (
            <></>
          )}

          {newsToDelete && (
            <Modal title="" active={deleteModalActive} onClose={handleCloseDeleteModal}>
              <p>Are you sure you want to delete "{newsToDelete.subject}"?</p>
              <div className="checkbox_wrap">
                <label>
                  <input
                    type="checkbox"
                    checked={retainTranscription}
                    onChange={e => setRetainTranscription(e.target.checked)}
                  />{' '}
                  Check the box if you would like to retain the associated transcript after deleting news.
                </label>
              </div>
              <div className="button_side_wrap">
                <BixButton type="button" onClick={handleDeleteNews}>
                  Delete News
                </BixButton>
                <BixButton light type="button" onClick={handleCloseDeleteModal}>
                  Cancel
                </BixButton>
              </div>
            </Modal>
          )}

          {isCreditSightsNewsIntegrationEnabled && newsToPublish && (
            <Modal title="" active={publishModalActive} onClose={handleClosePublishModal}>
              <p>Are you sure you want to republish "{newsToPublish.subject}" to CreditSights?</p>
              <div className="button_side_wrap">
                <BixButton type="button" onClick={handlePublishNews}>
                  Publish News
                </BixButton>
                <BixButton light type="button" onClick={handleClosePublishModal}>
                  Cancel
                </BixButton>
              </div>
            </Modal>
          )}
        </div>
      );
    }
  };

  return <section>{renderNews()}</section>;
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    dispatchRemoveNews: (news: Bixby.Models.AdminNews, retainTranscription: boolean) =>
      dispatch(removeNews(news, retainTranscription)),
    dispatchPublishNews: (news: Bixby.Models.AdminNews) => dispatch(publishNews(news)),
  };
};

const mapStateToProps = (state: AppState) => {
  const { isCreditSightsNewsIntegrationEnabled } = getCreditSightsNewsIntegrationEnabled(state);

  return {
    filters: (state.newsFiltersState.data || []).map(filter => {
      const { id, name } = filter;
      return { id, name, active: false } as IFilterItem;
    }),
    settings: state.settingsState.settings,
    isNewsLoading: state.newsAdminState.isLoading,
    isCompanyLoading: state.companyState.isLoading,
    currentCompany: state.companyState.currentCompany,
    isCreditSightsNewsIntegrationEnabled: isCreditSightsNewsIntegrationEnabled,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(CompanyNews);
